import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { areaForPage, areaForFamily } from '../utils/sanityAreas';
import { formatDateTime } from '../utils/format';
import { IcoLn } from '../shell/ActionIcons';
import { Emo } from '../shell/EmojiIcons';
import { shellRead } from '../shell/shellRead';   // Round 4e: the shell's one request, flushed after paint

// Transient cache: the family-summary is ONE global snapshot (the same for every page — only the
// display is filtered per page), so fetch it once and reuse across mounts/navigations. It refreshes
// only when sanity actually changes (the events below), never on every page open.
let summaryCache = null;

// 241 SANITY-ALERTS-PER-MODULE — surface a module-relevant sanity alert where the user works, so
// problems find the user. READ-ONLY: reads the persisted per-family summary (GET /api/sanity/family-
// summary, a single-row cheap read — no rule evaluation, no full /api/sanity pass). Rendered ONCE from
// App's <main> above the current page; a module with no red/amber renders NOTHING.
//
// 242B — the family->area map now lives in ONE place (utils/sanityAreas) shared with the report's area
// filters/grouping, so alerts and filters use one taxonomy.

export default function ModuleAlert({ page, onNavigate, onCollapse }) {
  // 41136 (GES-253): this banner renders above EVERY page, so it was the single most-repeated
  // English string the switcher-audit found. `area.label` and the rule ids (A4, F12, …) stay
  // English — they are the sanity taxonomy's own names, which the report itself is keyed on.
  const { t } = useTranslation();
  const [summary, setSummary] = useState(summaryCache);   // { families, computed_at }
  const [dismissed, setDismissed] = useState(false);

  // 292 — refresh after a sanity RE-RUN, so the alert reflects the run the user just triggered rather
  // than the previous one. This component is mounted in App, ABOVE the page, so the re-run (inside
  // SanityReportPage) reaches it via the codebase's existing gestion:* CustomEvent convention.
  //
  // THE DEGRADED CASE NEEDS NO SPECIAL HANDLING, and that is the point: 241's persistSanityFamilySummary
  // SKIPS THE WRITE on a degraded / A4_INDETERMINATE run, keeping the last good summary. So a re-read
  // after a degraded run returns that last good summary automatically. The invariant lives at the WRITE,
  // and every reader inherits it — a reader cannot defeat it by re-reading, and must not try to
  // second-guess it here.
  // 515 FIX 2 (freshness): re-read the snapshot on mount, on NAVIGATION (page change), and on a
  // sanity-changed event — instead of fetch-once — so the banner reflects current integrity WITHOUT a hard
  // refresh (511). Cheap single-row read (NOT the heavy live /api/sanity). `gestion:sanity-rerun-complete`
  // (fired by SanityReportPage after a re-run) is one such signal; `gestion:sanity-changed` is the general one.
  useEffect(() => {
    const read = () => shellRead('sanity_summary').then(s => { summaryCache = s; setSummary(s); }).catch(() => {});
    if (!summaryCache) read();   // fetch once per session, after first paint (Round 1 C1; Round 4e: shellRead defers it); events below refresh it on real changes
    window.addEventListener('gestion:sanity-rerun-complete', read);
    window.addEventListener('gestion:sanity-changed', read);
    return () => {
      window.removeEventListener('gestion:sanity-rerun-complete', read);
      window.removeEventListener('gestion:sanity-changed', read);
    };
  }, []);   // once — not per navigation; the summary is global and cached
  useEffect(() => { setDismissed(false); }, [page]);   // a new page's alert isn't pre-dismissed

  if (!summary || dismissed) return null;
  const area = areaForPage(page);
  // 515 FIX 1 (coverage): the Reports integrity banner surfaces ALL firing families (mirror the nav badge),
  // not just its page's area subset — else a red Finance rule (F3/F13) or any non-Inventory rule NEVER shows
  // on Reports (511). Other module pages keep their area-scoped per-module alert (241 design).
  const showAll = page === 'reports';
  if (!area && !showAll) return null;
  const fams = summary.families || {};
  const famKeys = showAll ? Object.keys(fams) : area.families;
  const hit = famKeys.filter(f => fams[f] && (fams[f].red || fams[f].amber));
  if (!hit.length) return null;   // green module -> render nothing (238/240 declutter principle)

  let red = 0, amber = 0; const ids = [];
  for (const f of hit) { red += fams[f].red || 0; amber += fams[f].amber || 0; ids.push(...(fams[f].ids || [])); }
  const hitAreas = [...new Set(hit.map(f => areaForFamily(f)?.label).filter(Boolean))];   // areas of the firing families
  const n = red + amber;
  const asOf = summary.computed_at ? formatDateTime(summary.computed_at) : null;
  const tone = red > 0
    ? { bg: '#fef2f2', border: '#fecaca', color: '#dc2626' }
    : { bg: '#fffbeb', border: '#fde68a', color: '#92400e' };

  const view = () => {
    // Deep-link: seed the report's AREA filter (242B — one taxonomy), then open it. SanityReportPage
    // reads + clears this on mount, so a later manual visit isn't stuck on the filter. 515 FIX 1: the
    // all-families Reports banner opens the report UNFILTERED (no single area to scope to).
    try {
      // 515-followup: land on the report PRE-FILTERED to Problems (red+amber) and to the areas of the
      // firing families — so clicking the banner shows exactly what it flagged, not the whole report.
      sessionStorage.setItem('sanity_filter_severity', 'problems');
      const areas = showAll ? hitAreas : (area ? [area.label] : []);
      if (areas.length) sessionStorage.setItem('sanity_filter_areas', JSON.stringify(areas));
      else sessionStorage.removeItem('sanity_filter_areas');
    } catch { /* ignore */ }
    onNavigate && onNavigate('sanity');
  };

  const btn = { fontSize: 12, fontWeight: 600, padding: '3px 10px', borderRadius: 6, cursor: 'pointer', border: `1px solid ${tone.border}`, background: '#fff', color: tone.color };
  return (
    <div role="status" style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '9px 14px', marginBottom: 16, borderRadius: 8, background: tone.bg, border: `1px solid ${tone.border}`, color: tone.color, fontSize: 13 }}>
      <span aria-hidden="true" style={{ fontWeight: 700 }}><Emo name="warn" /></span>
      <span style={{ fontWeight: 600 }}>
        {t('shared.moduleAlert.issues', { count: n })}{showAll ? '' : t('shared.moduleAlert.inArea', { area: area.label })}
        <span style={{ fontWeight: 400, opacity: 0.85 }}> — {ids.join(', ')}</span>
      </span>
      {asOf && <span style={{ fontSize: 11, opacity: 0.7, whiteSpace: 'nowrap' }}>{t('shared.moduleAlert.asOf', { when: asOf })}</span>}
      <button onClick={view} style={{ ...btn, marginLeft: 'auto' }}>{t('shared.moduleAlert.view')}</button>
      <button onClick={() => (onCollapse ? onCollapse() : setDismissed(true))} title={t('shared.moduleAlert.dismissTitle')} aria-label={t('shared.moduleAlert.dismiss')} style={{ ...btn, padding: '3px 8px' }}><IcoLn name="close" /></button>
    </div>
  );
}
